import blobStream from '../pdf/blob-stream.js';
import { fetchWebFont } from '../pdf/helpers';
import { createPresalePdf, createQuotePdf, createRimAppraisalPdf } from '../pdf/index';

declare const Blazor;

export async function loadBlazor() {
	window['blazorRegisterServiceWorker'] = async (caller, methodName: string) => {
		if (!('serviceWorker' in navigator)) {
			const errorMessage = `This browser doesn't support service workers`;
			console.error(errorMessage);
			throw errorMessage;
		}

		console.log('Service worker: Register');

		try {
			const registration = await navigator.serviceWorker.register('/service-worker.js');

			setInterval(() => {
				registration.update();
			}, 60 * 1000);

			registration.onupdatefound = () => {
				console.log('Service worker: Update found');

				const installingServiceWorker = registration.installing;

				if (installingServiceWorker) {
					console.log('Service worker: Installing');

					installingServiceWorker.onstatechange = () => {
						console.log('Service worker: ' + installingServiceWorker.state);

						if (installingServiceWorker.state === 'installed') {
							caller.invokeMethodAsync(methodName, !!navigator.serviceWorker.controller);
						}
					};
				}
			};
		} catch (error) {
			console.error('Service worker registration failed with error:', error);
			throw error;
		}
	};

	window['blazorDispatchJSEvent'] = (element: HTMLElement, eventName: string) => {
		element?.dispatchEvent(new Event(eventName));
	};

	window['blazorDispatchGTEvent'] = (element: HTMLElement, eventName: string) => {
		if (!element) {
			return;
		}

		if (element.attributes?.length && Array.from(element.attributes).find(o => o.name.startsWith('data-gt-component-initialized'))) {
			element.dispatchEvent(new Event(eventName));
			return;
		}

		element.addEventListener('gt-component-initialized', () => {
			element.dispatchEvent(new Event(eventName));
		});
	};

	window['blazorBindJSEvent'] = (element: HTMLElement, caller, eventName: string, methodName: string) => {
		if (!element) {
			return;
		}

		element.addEventListener(eventName, () => {
			caller.invokeMethodAsync(methodName);
		});
	};

	window['blazorBindMultipleSelect'] = (element: HTMLElement, caller, eventName: string, methodName: string) => {
		if (!element) {
			return;
		}

		element.addEventListener(eventName, () => {
			const select = element.dataset['select'] ? document.getElementById(element.dataset['select']) : null;
			caller.invokeMethodAsync(methodName, select?.dataset['value']?.split(','));
		});
	};

	const closeDialog = (event: KeyboardEvent) => {
		let isEscape = false;
		if ('key' in event) {
			isEscape = event.key === 'Escape' || event.key === 'Esc';
		} else if ('keyCode' in event) {
			isEscape = (event as KeyboardEvent).keyCode === 27;
		}

		if (isEscape) {
			const dialogs = Array.from(document.querySelectorAll('.c-dialog'));

			if (dialogs.length <= 1) {
				document.removeEventListener('keydown', closeDialog);
				document.body.classList.remove('c-dialog--no-scroll');
			}

			for (const dialog of dialogs) {
				dialog.dispatchEvent(new Event('op10.close'));
			}
		}
	};

	window['blazorOpenDialog'] = (options: any) => {
		if (!options || !options.disableClose) {
			document.removeEventListener('keydown', closeDialog);
			document.addEventListener('keydown', closeDialog);
		}

		document.body.classList.add('c-dialog--no-scroll');
	};

	window['blazorCloseDialog'] = () => {
		const dialogs = Array.from(document.querySelectorAll('.c-dialog'));

		if (dialogs.length <= 1) {
			document.removeEventListener('keydown', closeDialog);
			document.body.classList.remove('c-dialog--no-scroll');
		}
	};

	window['blazorOpenFileStream'] = async (contentStreamReference: Body, contentType: string) => {
		const arrayBuffer = await contentStreamReference.arrayBuffer();
		const blob = new Blob([arrayBuffer], { type: contentType });
		const url = URL.createObjectURL(blob);
		window.open(url);
	};

	window['blazorDownloadFileStream'] = async (contentStreamReference: Body, fileName: string) => {
		const arrayBuffer = await contentStreamReference.arrayBuffer();
		const blob = new Blob([arrayBuffer]);
		const url = URL.createObjectURL(blob);
		const anchorElement = document.createElement('a');
		anchorElement.href = url;
		anchorElement.download = fileName ?? '';
		anchorElement.click();
		anchorElement.remove();
		URL.revokeObjectURL(url);
	};

	window['blazorDownloadFile'] = (bytes: Uint8Array, fileName: string, contentType: string) => {
		const blob = new Blob([bytes], { type: contentType });
		const url = URL.createObjectURL(blob);
		const anchorElement = document.createElement('a');
		anchorElement.href = url;
		anchorElement.download = fileName ?? '';
		anchorElement.click();
		anchorElement.remove();
		URL.revokeObjectURL(url);
	};

	window['blazorGetSelection'] = () => {
		return window.getSelection()?.toString();
	};

	window['blazorGetBrowserLanguage'] = () => {
		return navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language;
	};

	window['blazorSetBrowserLanguage'] = (lang: string) => {
		return (document.documentElement.lang = lang);
	};

	window['blazorBindChangeLocale'] = (caller, methodName: string, location: string) => {
		window['blazorChangeLocale'] = async (locale: string) => {
			await caller.invokeMethodAsync(methodName, locale);
			window.location.href = location;
		};
	};

	const waitForElement = (parent: HTMLElement, selector: string, tries: number) => {
		return new Promise<HTMLElement | null>(resolve => {
			let element = parent?.querySelector<HTMLElement>(selector);

			if (element) {
				resolve(element);
				return;
			}

			const interval = setInterval(() => {
				element = parent?.querySelector<HTMLElement>(selector);

				if (tries <= 0 || element) {
					clearInterval(interval);
					resolve(element);
				} else {
					tries--;
				}
			}, 150);
		});
	};

	window['blazorFocusFirstInputValidationError'] = (element: HTMLElement) => {
		requestAnimationFrame(async () => {
			let error = await waitForElement(element, '.input-validation-error', 5);

			if (!error) {
				return;
			}

			const parent = error.closest<HTMLElement>('.row') || error.closest<HTMLElement>('.c-form');

			if (parent) {
				error = parent;
			}

			if (!error) {
				return;
			}

			const headerOffset = Number(getComputedStyle(document.documentElement).getPropertyValue('--nax-height').replace('px', ''));
			const elementPosition = error.getBoundingClientRect().top;

			window.scrollTo({
				top: elementPosition + window.scrollY - headerOffset,
				behavior: 'smooth'
			});
		});
	};

	window['blazorDownloadQuotePdf'] = async (body: any, fileName: string) => {
		const pdf = await createQuotePdf(body, async pdf => {
			await fetchWebFont(pdf);
		});

		const stream = pdf.pipe(blobStream());

		pdf.end();

		stream.on('finish', () => {
			const url = stream.toBlobURL('application/pdf');

			const anchorElement = document.createElement('a');
			anchorElement.href = url;
			anchorElement.download = fileName ?? '';
			anchorElement.click();
			anchorElement.remove();
			URL.revokeObjectURL(url);
		});
	};

	window['blazorCreateQuotePdfPreview'] = async (iframe: HTMLIFrameElement, body: any) => {
		const pdf = await createQuotePdf(body, async pdf => {
			await fetchWebFont(pdf);
		});

		const stream = pdf.pipe(blobStream());

		pdf.end();

		stream.on('finish', () => {
			iframe.src = `${stream.toBlobURL('application/pdf')}#toolbar=0`;
		});
	};

	window['blazorGetQuotePdfStream'] = async (body: any) => {
		const pdf = await createQuotePdf(body, async pdf => {
			await fetchWebFont(pdf);
		});

		const stream = pdf.pipe(blobStream());

		pdf.end();

		return await new Promise((resolve, reject) => {
			stream.on('finish', () => {
				const blob = stream.toBlob('application/pdf');

				blob.arrayBuffer()
					.then(buf => {
						resolve(new Uint8Array(buf));
					})
					.catch(() => reject());
			});

			stream.on('error', () => {
				reject();
			});
		});
	};

	window['blazorDownloadPresalePdf'] = async (body: any, fileName: string) => {
		const pdf = await createPresalePdf(body, async pdf => {
			await fetchWebFont(pdf);
		});

		const stream = pdf.pipe(blobStream());

		pdf.end();

		stream.on('finish', () => {
			const url = stream.toBlobURL('application/pdf');

			const anchorElement = document.createElement('a');
			anchorElement.href = url;
			anchorElement.download = fileName ?? '';
			anchorElement.click();
			anchorElement.remove();
			URL.revokeObjectURL(url);
		});
	};

	window['blazorCreatePresalePdfPreview'] = async (iframe: HTMLIFrameElement, body: any) => {
		const pdf = await createPresalePdf(body, async pdf => {
			await fetchWebFont(pdf);
		});

		const stream = pdf.pipe(blobStream());

		pdf.end();

		stream.on('finish', () => {
			iframe.src = `${stream.toBlobURL('application/pdf')}#toolbar=0`;
		});
	};

	window['blazorGetPresalePdfStream'] = async (body: any) => {
		const pdf = await createPresalePdf(body, async pdf => {
			await fetchWebFont(pdf);
		});

		const stream = pdf.pipe(blobStream());

		pdf.end();

		return await new Promise((resolve, reject) => {
			stream.on('finish', () => {
				const blob = stream.toBlob('application/pdf');

				blob.arrayBuffer()
					.then(buf => {
						resolve(new Uint8Array(buf));
					})
					.catch(() => reject());
			});

			stream.on('error', () => {
				reject();
			});
		});
	};

	window['blazorGetRimAppraisalPdfStream'] = async (body: any) => {
		const pdf = await createRimAppraisalPdf(body, async pdf => {
			await fetchWebFont(pdf);
		});

		const stream = pdf.pipe(blobStream());

		pdf.end();

		return await new Promise((resolve, reject) => {
			stream.on('finish', () => {
				const blob = stream.toBlob('application/pdf');

				blob.arrayBuffer()
					.then(buf => {
						resolve(new Uint8Array(buf));
					})
					.catch(() => reject());
			});

			stream.on('error', () => {
				reject();
			});
		});
	};

	window['blazorDownloadRimAppraisalPdf'] = async (body: any, fileName: string) => {
		const pdf = await createRimAppraisalPdf(body, async pdf => {
			await fetchWebFont(pdf);
		});

		const stream = pdf.pipe(blobStream());

		pdf.end();

		stream.on('finish', () => {
			const url = stream.toBlobURL('application/pdf');

			const anchorElement = document.createElement('a');
			anchorElement.href = url;
			anchorElement.download = fileName ?? '';
			anchorElement.click();
			anchorElement.remove();
			URL.revokeObjectURL(url);
		});
	};

	if (typeof Blazor !== 'undefined') {
		Blazor.start();
	}
}
